/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { createSelector } from '@reduxjs/toolkit';
import { standards } from '@g17eco/core';
import { RootState } from '../reducers';
import { naturalSort } from '../utils';
import { SunburstDataItem } from '../reducers/reporting-frameworks';

export const selectByFramework = createSelector(
  [
    (state: RootState) => state.reportingFrameworks,
  ],
  ({ loaded, data }) => (frameworkCode: string) => {

    if (!loaded || !data) {
      return;
    }

    if (frameworkCode === 'sdg' || !frameworkCode) {
      return data.map;
    }

    const frameworks = data.frameworks;

    const standard = standards[frameworkCode];
    const map: SunburstDataItem = {
      name: standard?.shortName ?? frameworkCode.toUpperCase(),
      type: frameworkCode,
      code: '',
      children: []
    };

    const framework = frameworks.find(f => f.code === frameworkCode);
    if (!framework) {
      return;
    }

    const sortedCodes = new Set<string>();
    framework.items?.forEach(c => {
      c.sdgCodes.forEach(sdgCode => {
        const [sdgGoal, sdgTarget] = sdgCode.split('.', 3);
        if (!sdgGoal) {
          return;
        }
        sortedCodes.add(sdgGoal);
        if (sdgTarget) {
          sortedCodes.add(`${sdgGoal}.${sdgTarget}`);
        }
      });
    })

    Array.from(sortedCodes)
      .sort(naturalSort)
      .forEach((sdgCode) => {
      const [sdgGoal, isTarget] = sdgCode.split('.', 2);

      let goalChild = map.children?.find(c => c.code === sdgGoal);
      if (!goalChild) {
        goalChild = {
          name: `SDG ${sdgGoal}`,
          type: frameworkCode,
          code: sdgGoal,
          value: 1,
          children: []
        }
        map.children?.push(goalChild);
      }
      if (isTarget) {
        goalChild.value = 0;
        let targetChild = goalChild.children?.find(c => c.code === sdgCode);
        if (!targetChild) {
          targetChild = {
            name: `SDG ${sdgCode}`,
            type: frameworkCode,
            code: sdgCode,
            value: 1
          }
          goalChild.children?.push(targetChild);
        }
      }
    });

    return map;
  }
)
